import { useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "i18n-fe";
import SignatureCanvas from "react-signature-canvas";

// #732/#804 — judge signature capture for the HUGE (~75% viewport width)
// signature modal in JudgeManager. #732's hand-rolled canvas mixed
// getBoundingClientRect() (border-box, includes the 1px CSS border) with a
// manually-set canvas.width buffer that ignored both the border and
// devicePixelRatio, so drawn ink appeared nowhere near the cursor (#804).
// react-signature-canvas (wraps signature_pad) derives the drawing buffer
// from the canvas element's own offsetWidth/offsetHeight and scales by
// devicePixelRatio, and reads pointer position from that same element — so
// buffer and pointer are always in agreement. Container-measured width and
// the ~320 height default are preserved (still measured the same way #732
// did it), as is the onChange(dataUrl|null) contract, so JudgeSignatureModal
// needs no changes.
export default function JudgeSignatureCanvas({ onChange, height = 320 }) {
  const { t } = useTranslation("scoreadmin");
  const theme = useTheme();
  const wrapRef = useRef(null);
  const sigRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hasInk, setHasInk] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const w = Math.floor(wrapRef.current?.clientWidth ?? 0);
      if (w) setWidth(w);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const emitChange = () => {
    const pad = sigRef.current;
    const empty = !pad || pad.isEmpty();
    setHasInk(!empty);
    onChange(empty ? null : pad.toDataURL("image/png"));
  };

  const clear = () => {
    sigRef.current?.clear();
    setHasInk(false);
    onChange(null);
  };

  return (
    <Box ref={wrapRef}>
      {width > 0 && (
        <SignatureCanvas
          // Remount whenever the measured width changes (eg. window resize
          // while the modal is open) so the drawing buffer is re-derived
          // from the current layout rather than left stale.
          key={`${width}x${height}`}
          ref={sigRef}
          penColor="#111111"
          clearOnResize={false}
          onEnd={emitChange}
          canvasProps={{
            "data-testid": "judge-signature-canvas",
            style: {
              display: "block",
              boxSizing: "border-box",
              width: `${width}px`,
              height: `${height}px`,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.shape.borderRadius,
              background: "#ffffff",
              touchAction: "none",
              cursor: "crosshair",
            },
          }}
        />
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {t("judges.signatureModal.drawHint")}
        </Typography>
        <Button size="small" color="inherit" onClick={clear} disabled={!hasInk}>
          {t("judges.signatureModal.clear")}
        </Button>
      </Stack>
    </Box>
  );
}
